import { useEffect, useState } from "react";
import { Button, Drawer, message } from "antd";
import { ArrowLeftOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { type DsmScopeItem } from "design-scope";
import {
  DESIGN_SCOPE_FEATURE_TYPE_ID,
  updateDesignScopeInstances
} from "services/design-scope-apis";
import { buildBulkBaseline } from "../shared/bulk-edit-design-scope-merge";
import { toBulkEditResponsibilityUpdateInstance } from "../shared/design-scope-create-mappers";
import { DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE } from "../shared/design-scope-messages";
import {
  findResponsibilityOption,
  type AddDesignScopeResponsibility,
  type DesignScopeResponsibilityOption
} from "../shared/design-scope-responsibilities";
import BulkSelectionViewPanel from "./bulk-selection-view-panel";
import { Field } from "./scope-item-field";
import ScopeResponsibilitySelect from "./scope-responsibility-select";
import "./scope-item.scss";

export type BulkEditResponsibilityDrawerProps = {
  open: boolean;
  selectedRows: DsmScopeItem[];
  projectToken: string;
  /** Project responsibility catalog. Omit to keep the built-in list. */
  responsibilities?: DesignScopeResponsibilityOption[];
  responsibilitiesLoading?: boolean;
  onAddResponsibility?: AddDesignScopeResponsibility;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
};

export default function BulkEditResponsibilityDrawer(
  props: BulkEditResponsibilityDrawerProps
) {
  const {
    open,
    selectedRows,
    projectToken,
    responsibilities,
    responsibilitiesLoading,
    onAddResponsibility,
    onClose,
    onSaved
  } = props;

  const [responsibility, setResponsibility] = useState("");
  const [baseline, setBaseline] = useState("");
  const [inProgress, setInProgress] = useState(false);

  const selectionKey = selectedRows.map((r) => r.id).join(",");

  useEffect(() => {
    if (!open) return;
    if (selectedRows.length === 0) {
      onClose();
      return;
    }
    const shared = buildBulkBaseline(selectedRows).responsibility ?? "";
    setBaseline(shared);
    setResponsibility(shared);
    // Reset when the drawer opens or the selection identity changes — not on
    // every parent re-render of the selectedRows array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey covers row ids
  }, [open, selectionKey, onClose]);

  const changed = responsibility.trim() !== baseline.trim();
  const canSave = selectedRows.length > 0 && changed;

  const selectValue = responsibilities
    ? (findResponsibilityOption(responsibilities, responsibility)?.id ??
      responsibility)
    : responsibility;

  const onSave = async () => {
    if (!canSave || inProgress) return;
    if (!projectToken) {
      message.error("Project auth is not ready. Please try again.");
      return;
    }
    setInProgress(true);
    try {
      const name = responsibility.trim();
      const feature_instances = selectedRows.map((row) => {
        let designerId: string | undefined;
        if (responsibilities) {
          const match = name
            ? findResponsibilityOption(responsibilities, name)
            : undefined;
          if (match) designerId = match.id;
          else if (!name) designerId = "";
        }
        return toBulkEditResponsibilityUpdateInstance(
          row.id,
          name,
          row.commitments,
          row.bids,
          designerId
        );
      });
      const response = await updateDesignScopeInstances(
        {
          feature_type: "DESIGN_SCOPE",
          feature_instance_type_id: DESIGN_SCOPE_FEATURE_TYPE_ID,
          feature_instances
        },
        projectToken
      );
      if (!response.success) {
        message.error(response.errorMsg || "Unable to update responsibility.");
        return;
      }
      message.success(DESIGN_SCOPE_UPDATE_SUCCESS_MESSAGE);
      await onSaved();
    } catch (err) {
      console.error(err);
      message.error("Unable to update responsibility.");
    } finally {
      setInProgress(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (!inProgress) onClose();
      }}
      size={560}
      closable={false}
      mask={{ closable: false }}
      destroyOnHidden
      className="dsm-edit"
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Back"
            data-testid="scope-bulk-edit-responsibility-back"
            className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-ciq-primary-900"
            onClick={() => {
              if (!inProgress) onClose();
            }}
          >
            <ArrowLeftOutlined />
          </button>
          <span className="text-base font-medium text-black text-opacity-90">
            Bulk Edit Responsibility
          </span>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={inProgress}
            data-testid="scope-bulk-edit-responsibility-cancel"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={!canSave || inProgress}
            loading={inProgress}
            onClick={() => void onSave()}
            data-testid="scope-bulk-edit-responsibility-submit"
          >
            Save
          </Button>
        </div>
      }
    >
      <div data-testid="scope-bulk-edit-responsibility-drawer">
        <div className="mb-4 flex items-start gap-2 text-sm text-black text-opacity-50">
          <InfoCircleOutlined className="mt-0.5 shrink-0" />
          <span>
            All selected Design Scope items will be updated to this
            responsibility.
          </span>
        </div>

        <BulkSelectionViewPanel
          items={selectedRows}
          data-testid="scope-bulk-edit-responsibility"
        />

        <Field label="Responsibility">
          <ScopeResponsibilitySelect
            className="max-w-full"
            style={{ width: "100%" }}
            testId="scope-bulk-edit-responsibility-field"
            value={selectValue}
            catalog={responsibilities}
            catalogLoading={responsibilitiesLoading}
            onAdd={
              onAddResponsibility
                ? async (name) => {
                    const result = await onAddResponsibility(name);
                    if (result.success && result.name) {
                      setResponsibility(result.name);
                    }
                    return result;
                  }
                : undefined
            }
            onChange={(next) => {
              if (!responsibilities) {
                setResponsibility(next);
                return;
              }
              if (!next) {
                setResponsibility("");
                return;
              }
              const match = responsibilities.find(
                (option) => option.id === next
              );
              if (match) setResponsibility(match.name);
            }}
          />
        </Field>
      </div>
    </Drawer>
  );
}
